<template>
  <v-container fluid class="theme_background mt-2 borderRadius">
    <v-row>
      <v-col cols="12" class="px-0">
        <p class="ml-2 mb-3 right_panel_title">
          <v-avatar color="rgb(var(--v-theme-dark-color))" size="small" class="pa-0 mr-2">
            <v-icon color="rgb(var(--v-theme-lighten))" large>mdi-slope-uphill</v-icon>
          </v-avatar>
          {{ $t('views.topo.slope') }}
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="px-0 py-0 borderRadius theme_surface_background">
        <div class="d-flex justify-space-between">
          <p class="ml-2 py-2 bodyTextsSmall">
            {{ $t('views.topo.slopeIgnition') }}
          </p>
          <p class="mr-1 py-1">
            {{ displayOrigin }}
          </p>
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-5 pt-3 pb-1 bodyTextsSmall">
      <p class="ml-3">{{ $t('views.topo.slopeArea') }}</p>
    </v-row>
    <v-row class="justify-space-between mt-4">
      <v-col class="mr-1 pt-2 px-0 py-1 text-center theme_surface_background borderRadius lineHeight16">
        <p>{{ displayMin }}</p>
        <label class="bodyTextsSmall">{{ $t('views.topo.minimum') }}</label>
      </v-col>
      <v-col class="mr-1 pt-2 px-0 py-1 text-center theme_surface_background borderRadius lineHeight16">
        <p>{{ displayMedian }}</p>
        <label class="bodyTextsSmall">{{ $t('views.topo.median') }}</label>
      </v-col>
      <v-col class="px-0 pt-2 py-1 text-center theme_surface_background borderRadius lineHeight16">
        <p>{{ displayMax }}</p>
        <label class="bodyTextsSmall">{{ $t('views.topo.maximum') }}</label>
      </v-col>
    </v-row>
    <v-row v-if="seriesComputed" class="mt-4 bodyTexts">
      <v-expansion-panels>
        <v-expansion-panel v-for="i in 1" :key="i">
          <v-expansion-panel-title class="theme_lighten">
            {{ $t('views.topo.details') }}
          </v-expansion-panel-title>
          <v-expansion-panel-text class="pb-4">
            <apexchart :key="theme" type="donut" :options="chartOptionsElevation" :series="seriesComputed"></apexchart>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
// Settings
import { storeToRefs } from 'pinia'

import settingsStore from '@/stores/settingsStore.ts'
const settings = settingsStore()
const { theme } = storeToRefs(settings)

import type { PropType } from 'vue'
import { computed, onMounted } from 'vue'

import { useI18n } from 'vue-i18n'

import type { ValueBufferType } from '@/types/api/Calculations'
import { getColorSteps } from '@/utils/gradientCalc'

const props = defineProps({
  data: {
    type: Object as PropType<ValueBufferType | undefined>,
    required: true
  }
})

const { t } = useI18n()

const notAvailable = computed(() => t('common.concepts.notAvailable'))
const displayOrigin = computed(() => (props.data?.Origin != null ? props.data.Origin.toFixed() + ' m' : notAvailable.value))
const displayMin = computed(() => (props.data?.Min != null ? props.data.Min.toFixed() + '%' : notAvailable.value))
const displayMedian = computed(() => (props.data?.Median != null ? props.data.Median.toFixed() + '%' : notAvailable.value))
const displayMax = computed(() => (props.data?.Max != null ? props.data.Max.toFixed() + '%' : notAvailable.value))

const seriesComputed = computed(() => {
  if (!props.data) return null
  const series = props.data.computedHist.map((item) => item.value.value)
  return series
})

const namesComputed = computed(() => {
  if (!props.data) return null
  const names = props.data.computedHist.map((item) => item.key)
  return names
})

const chartOptionsElevation = computed(() => {
  const strokeColors: Record<string, string> = {
    light: '#cccccc',
    dark: '#2f2f2f'
  }
  return {
    labels: namesComputed.value,
    colors: [] as string[],
    legend: {
      position: 'bottom',
      fontSize: '10px',
      labels: {
        colors: 'rgb(var(--v-theme-lighten))'
      },
      offsetY: 0
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80'
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false
    },
    height: '400',
    stroke: {
      show: true,
      colors: [strokeColors[theme.value]],
      width: 2.5
    },
    fill: {
      pattern: {
        width: 60,
        height: 6,
        strokeWidth: 4
      }
    }
  }
})

onMounted(() => {
  chartOptionsElevation.value.colors = getColorSteps(/*props.data?.computedHist[0].value.endColor,*/ '#38d9a9', '#FFFFFF', 6)
})
</script>

<style lang="scss" scoped></style>
